"use client";

import { Alert, Stack } from "@mui/material";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormTextField } from "@rmh/ds";
import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { TFn } from "../../types";
import { OnboardingFormWrapper } from "../../layout";
import { FormHeader } from "../../layout/FormHeader";
import { OnboardingSubmitButton } from "../OnboardingSubmitButton";
import { AuthSupportFooter } from "./AuthSupportFooter";

const getSchema = (t: TFn) =>
  z.object({
    identifier: z
      .string()
      .min(1, t("error.identifierRequired"))
      .email(t("error.invalidEmail")),
  });

export type MagicSignInRequestValues = z.infer<ReturnType<typeof getSchema>>;

const defaultValues: MagicSignInRequestValues = { identifier: "" };

interface MagicSignInRequestFormProps {
  onSubmit: (values: MagicSignInRequestValues) => void;
  error?: string;
  isSubmitting?: boolean;
  /** Prefills the field when returning from the check-email step via "Edit". */
  initialEmail?: string;
}

export const MagicSignInRequestForm = ({
  onSubmit,
  error,
  isSubmitting = false,
  initialEmail,
}: MagicSignInRequestFormProps) => {
  const t = useTranslations("onboardingV2.magicSignIn.request");
  const schema = useMemo(() => getSchema(t), [t]);

  const methods = useForm<MagicSignInRequestValues>({
    resolver: zodResolver(schema),
    mode: "onSubmit",
    defaultValues: initialEmail ? { identifier: initialEmail } : defaultValues,
  });

  // RHF's handleSubmit invokes this with (values, event); wrap it so callers
  // (and tests) only ever see the parsed values, matching ContactStepForm's
  // pattern for the same Form/RHF integration.
  const handleSubmit = (values: MagicSignInRequestValues) => onSubmit(values);

  return (
    <OnboardingFormWrapper
      alignContentVertical="center"
      outerFooter={<AuthSupportFooter placement="outer" />}
      innerFooter={<AuthSupportFooter placement="inner" />}
      sx={{ paper: { justifyContent: "center" } }}
    >
      <Form methods={methods} onSubmit={handleSubmit}>
        <FormHeader title={t("title")} />
        <Stack className="form_body" spacing={4}>
          <FormTextField
            name="identifier"
            label={t("identifierLabel")}
            autoComplete="email"
            required
            hideRequiredMarker
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
        <OnboardingSubmitButton sxBox={{ padding: 0 }} loading={isSubmitting}>
          {t("submit")}
        </OnboardingSubmitButton>
      </Form>
    </OnboardingFormWrapper>
  );
};
